/** biome-ignore-all lint/complexity/noUselessFragments: <this is needed here> */

import type { HomeDto } from '@lmc/ts-common'
import {
  Button,
  FilterContainer,
  GroupedResidentsMultiSelect,
  MultiSelect,
  RangePicker
} from '@lmc/ui-kit'
import dayjs, { type Dayjs } from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import styled from 'styled-components'
import type {
  ChargeEventStatus,
  ChargeReconciliationFilters as ChargeReconciliationFiltersType,
  ChargeReconciliationSortField,
  ChargeReconciliationStatusValue
} from '../../../Reports/types/chargeReconciliation'
import {
  getChargeReconciliationRangeDisabledDate,
  isValidChargeReconciliationDateRange,
  RECONCILIATION_CHARGE_MAX_PAST_MONTHS
} from './chargeRatesUtils'

export const CHARGE_RECONCILIATION_PAGE_SIZE = 10

export function useChargeRatesFilters(nominative: string) {
  const defaultStart = dayjs().subtract(7, 'day')
  const defaultEnd = dayjs().subtract(1, 'day')

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    defaultStart,
    defaultEnd
  ])
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  )
  const [fundingTypeFilter, setFundingTypeFilter] = useState<string[]>([])
  const [serviceUserFilter, setServiceUserFilter] = useState<string[]>([])
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([])
  const [eventStatusFilter, setEventStatusFilter] = useState<string[]>([])
  const [warningsFilter, setWarningsFilter] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(
    undefined
  )
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const apiSortField = useMemo(():
    | ChargeReconciliationSortField
    | undefined => {
    if (!sortBy || !sortOrder) {
      return undefined
    }
    const m: Record<string, ChargeReconciliationSortField> = {
      status: 'status',
      date: 'date',
      fundingType: 'fundingType',
      serviceUser: 'serviceUser',
      teamMembers: 'teamMembers',
      eventType: 'eventType',
      plannedTime: 'plannedTime',
      eventStatus: 'eventStatus',
      warningCount: 'warningCount',
      chargedTime: 'chargedTime',
      chargeRate: 'chargeRate',
      chargeAmount: 'chargeAmount'
    }
    return m[sortBy]
  }, [sortBy, sortOrder])

  const filters: ChargeReconciliationFiltersType = useMemo(
    () => ({
      startTime: dateRange[0].startOf('day').toISOString(),
      endTime: dateRange[1].endOf('day').toISOString(),
      ...(statusFilter
        ? { status: statusFilter as ChargeReconciliationStatusValue }
        : {}),
      ...(fundingTypeFilter.length
        ? { fundingTypeIds: fundingTypeFilter }
        : {}),
      ...(serviceUserFilter.length
        ? { serviceUserIds: serviceUserFilter }
        : {}),
      ...(eventTypeFilter.length ? { eventTypeIds: eventTypeFilter } : {}),
      ...(eventStatusFilter.length
        ? { eventStatuses: eventStatusFilter as ChargeEventStatus[] }
        : {}),
      ...(warningsFilter.length ? { warnings: warningsFilter } : {}),
      ...(apiSortField !== undefined && sortOrder
        ? { sortField: apiSortField, sortOrder }
        : {}),
      page,
      pageSize: CHARGE_RECONCILIATION_PAGE_SIZE
    }),
    [
      dateRange,
      statusFilter,
      fundingTypeFilter,
      serviceUserFilter,
      eventTypeFilter,
      eventStatusFilter,
      warningsFilter,
      apiSortField,
      sortOrder,
      page
    ]
  )

  const hasActiveFilters =
    !!statusFilter ||
    fundingTypeFilter.length > 0 ||
    serviceUserFilter.length > 0 ||
    eventTypeFilter.length > 0 ||
    eventStatusFilter.length > 0 ||
    warningsFilter.length > 0

  const clearFilters = useCallback(() => {
    setStatusFilter(undefined)
    setFundingTypeFilter([])
    setServiceUserFilter([])
    setEventTypeFilter([])
    setEventStatusFilter([])
    setWarningsFilter([])
    setPage(1)
    setSelectedRowKeys([])
  }, [])

  return {
    filters,
    page,
    setPage,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    selectedRowKeys,
    setSelectedRowKeys,
    nominative,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    fundingTypeFilter,
    setFundingTypeFilter,
    serviceUserFilter,
    setServiceUserFilter,
    eventTypeFilter,
    setEventTypeFilter,
    eventStatusFilter,
    setEventStatusFilter,
    warningsFilter,
    setWarningsFilter,
    hasActiveFilters,
    clearFilters
  }
}

export function ChargeRatesFiltersBar({
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  fundingTypeFilter,
  setFundingTypeFilter,
  serviceUserFilter,
  setServiceUserFilter,
  eventTypeFilter,
  setEventTypeFilter,
  eventStatusFilter,
  setEventStatusFilter,
  warningsFilter,
  setWarningsFilter,
  setPage,
  setSelectedRowKeys,
  nominative,
  hasActiveFilters,
  clearFilters
}: ReturnType<typeof useChargeRatesFilters>) {
  const { t } = useTranslation()

  const { residents } = useResidentsList({})
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  // Funding types are already loaded on the home in redux (rota settings) — no
  // extra BE call needed here, same as elsewhere charge/funding types are read.
  const fundingTypes = useSelector(
    (state: { global: { home: HomeDto } }) =>
      state.global.home?.rotaSettings?.fundingTypes ?? []
  )

  const statusOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_STATUS_NEEDS_ATTENTION'),
        value: 'needs_attention'
      },
      { label: t('RECONCILIATION_STATUS_RECONCILED'), value: 'reconciled' }
    ],
    [t]
  )

  const fundingTypeOptions = useMemo(
    () =>
      fundingTypes
        .filter((fundingType) => fundingType.id && !fundingType.isDeleted)
        .map((fundingType) => ({
          label: fundingType.name,
          value: fundingType.id as string
        })),
    [fundingTypes]
  )

  const eventTypeOptions = useMemo(() => {
    type EventTypeItem = { name: string; id?: string; _id?: string }
    const all = [
      ...(shiftTypes ?? []).map((st: EventTypeItem) => ({
        label: st.name,
        value: st.id ?? st._id
      })),
      ...(visitTypes ?? []).map((vt: EventTypeItem) => ({
        label: vt.name,
        value: vt.id ?? vt._id
      }))
    ]
    return [...new Map(all.map((o) => [o.value, o])).values()]
  }, [shiftTypes, visitTypes])

  const eventStatusOptions = useMemo(
    () => [
      { label: t('RECONCILIATION_EVENT_STATUS_DONE'), value: 'DONE' },
      {
        label: t('RECONCILIATION_EVENT_STATUS_CANCELLED'),
        value: 'CANCELLED'
      },
      { label: t('RECONCILIATION_EVENT_STATUS_ONGOING'), value: 'ONGOING' },
      { label: t('RECONCILIATION_EVENT_STATUS_OVERDUE'), value: 'OVERDUE' }
    ],
    [t]
  )

  const warningsOptions = useMemo(
    () => [
      { label: t('CLOCK_IN_EARLY'), value: 'clockInEarly' },
      { label: t('CLOCK_IN_LATE'), value: 'clockInLate' },
      { label: t('CLOCK_OUT_EARLY'), value: 'clockOutEarly' },
      { label: t('CLOCK_OUT_LATE'), value: 'clockOutLate' },
      { label: t('SKILL_MISMATCH'), value: 'skillMismatch' },
      { label: t('CLOCK_IN_MISMATCH'), value: 'clockInMismatch' },
      { label: t('GENDER_MISMATCH'), value: 'genderMismatch' },
      {
        label: t('CLOCKED_IN_LOCATION_MISMATCH'),
        value: 'clockInLocationMismatch'
      },
      {
        label: t('CLOCKED_OUT_LOCATION_MISMATCH'),
        value: 'clockOutLocationMismatch'
      }
    ],
    [t]
  )

  const resetPage = () => {
    setPage(1)
    setSelectedRowKeys([])
  }

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      return
    }
    if (!isValidChargeReconciliationDateRange(dates[0], dates[1])) {
      return
    }
    setDateRange([dates[0], dates[1]])
    resetPage()
  }

  return (
    <FilterContainer title={t('FILTERS')}>
      <>
        <RangePicker
          value={dateRange}
          onChange={handleRangeChange}
          disabledDate={getChargeReconciliationRangeDisabledDate}
          allowClear={false}
          format="DD-MM-YYYY"
          minDate={dayjs().subtract(
            RECONCILIATION_CHARGE_MAX_PAST_MONTHS,
            'month'
          )}
          maxDate={dayjs()}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_TABLE_STATUS')}
          values={statusFilter ? [statusFilter] : []}
          options={statusOptions}
          onChange={(selected) => {
            const arr = selected as string[]
            setStatusFilter(arr.length ? arr[arr.length - 1] : undefined)
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_CHARGE_TABLE_FUNDING_TYPE')}
          values={fundingTypeFilter}
          options={fundingTypeOptions}
          hasClear
          hasSearch
          onChange={(selected) => {
            setFundingTypeFilter(selected as string[])
            resetPage()
          }}
        />
        <GroupedResidentsMultiSelect
          residents={residents}
          title={nominative}
          values={serviceUserFilter}
          handleInputChange={(selected) => {
            setServiceUserFilter(selected as string[])
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}
          values={eventTypeFilter}
          options={eventTypeOptions}
          hasClear
          hasSearch
          onChange={(selected) => {
            setEventTypeFilter(selected as string[])
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_TABLE_WARNINGS')}
          values={warningsFilter}
          options={warningsOptions}
          hasClear
          hasSearch
          onChange={(selected) => {
            setWarningsFilter(selected as string[])
            resetPage()
          }}
        />
        <NarrowFilterMultiSelect
          title={t('RECONCILIATION_CHARGE_TABLE_EVENT_STATUS')}
          values={eventStatusFilter}
          options={eventStatusOptions}
          hasClear
          onChange={(selected) => {
            setEventStatusFilter(selected as string[])
            resetPage()
          }}
        />
        {hasActiveFilters && (
          <ClearFiltersButton variant="text" onClick={clearFilters}>
            {t('CLEAR_FILTERS')}
          </ClearFiltersButton>
        )}
      </>
    </FilterContainer>
  )
}

const NarrowFilterMultiSelect = styled(MultiSelect)`
  min-width: 140px;
  width: 176px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    max-width: none;
  }
` as typeof MultiSelect

const ClearFiltersButton = styled(Button)`
  white-space: nowrap;
  align-self: center;
`
